/**
 * AI CONTEXT
 * Purpose: pure, unit-tested helpers for a single StandoutMomentCard —
 *   category → left-border color mapping, the needs-work visibility gate
 *   (lead teacher+ or the moment's own creator; a plain teacher must never
 *   see negative signal on a colleague's moment), the edit/generate
 *   eligibility gate (creator or lead teacher+), the artwork-strip
 *   selection logic (only scans from the moment's class session, newest
 *   first). No React, no Firebase — see standoutReviewHelpers.ts for the
 *   moment-level (non-card) helpers.
 */
import { sortBy } from "lodash"

import { getArtworkDisplayUrl } from "#components/forms/AddStudent/studentArtworkTilesHelpers"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import {
  type StandoutCategory,
  type StandoutMomentConstructor,
} from "#customTypes/composedTypes/standoutTypes"
import {
  ARTWORK_CAPTURED_BY,
  type StudentArtwork,
} from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"

// ---------------------------------------------------------------------------
// Category color (left border + tag pills)
// ---------------------------------------------------------------------------

/**
 * Fixed hexes rather than theme tokens: this file is pure/unit-tested and has
 * no React context. thumbsUp/needsWork mirror `theme.eric.green`/`.red`;
 * secret has no theme token, so it uses antd's own purple (`purple-6`).
 */
export const CATEGORY_COLORS: Record<StandoutCategory, string> = {
  thumbsUp: "#1D8921",
  needsWork: "#D44040",
  secret: "#722ed1",
}

/** Where a note or scan was written, as the small pill on a feed row. */
export const feedItemSourceLabel = (
  source: string | null | undefined
): string => {
  switch (source) {
    case "teacherApp":
      return "Teacher app"
    case "scanner":
    case "scannerApp":
      return "Scanner"
    case "parent":
      return "Parent app"
    case "student":
      return "Student"
    default:
      return "Jarvis"
  }
}

/** Role pill on a student-group feed row. A TA note wins over the staff role. */
export const feedStaffRole = (
  isAssistant: boolean,
  role: string | null | undefined
): string => {
  if (isAssistant || role === USER_ROLES.ASSISTANT) return "TA"
  if (role === USER_ROLES.LEAD_TEACHER) return "Lead teacher"
  return "Teacher"
}

/**
 * Byline for a class scan. Only a teacher-app scan names a person:
 * `createdBy` is the PIN teacher or the selected TA. The scanner stores the
 * kiosk account there, and a phone upload stores the student or guardian.
 * Those are not a byline. Missing on the teacher-app side means no name.
 */
export const classScanAuthor = (
  artwork: {
    createdBy?: string | null
    capturedBy?: string | null
  },
  usersById: Record<
    string,
    { name?: string | null; role?: string | null } | undefined
  >,
  studentsById: Record<
    string,
    { firstName?: string | null; lastName?: string | null } | undefined
  >
): { name: string; role: string } | null => {
  if (artwork.capturedBy !== ARTWORK_CAPTURED_BY.TEACHER_APP) return null
  const id = artwork.createdBy?.trim()
  if (!id) return null
  const userName = usersById[id]?.name?.trim()
  if (userName) {
    return { name: userName, role: feedStaffRole(false, usersById[id]?.role) }
  }
  const student = studentsById[id]
  const studentName = [student?.firstName, student?.lastName]
    .filter((part): part is string => !!part && part.trim().length > 0)
    .join(" ")
  if (studentName) return { name: studentName, role: "TA" }
  return null
}

/**
 * Student ids a teacher-app scan names as the person who took it. The
 * scanner kiosk and phone uploads are not people to look up.
 */
export const teacherAppScanAuthorIds = (
  students: readonly {
    artwork?: Record<
      string,
      { createdBy?: string | null; capturedBy?: string | null } | null
    > | null
  }[]
): string[] => {
  const ids = new Set<string>()
  for (const student of students) {
    for (const artwork of Object.values(student.artwork ?? {})) {
      if (!artwork) continue
      if (artwork.capturedBy !== ARTWORK_CAPTURED_BY.TEACHER_APP) continue
      const id = artwork.createdBy?.trim()
      if (id) ids.add(id)
    }
  }
  return sortBy([...ids])
}

// ---------------------------------------------------------------------------
// Permission gates
// ---------------------------------------------------------------------------

/**
 * Needs-work signal (badge count, who-marked tags, reaction note tooltips)
 * is visible only to LEAD_TEACHER+ (moderation permission) or the moment's
 * own creator — a plain teacher reviewing a colleague's moment must see zero
 * negative signal (owner ask).
 */
export const canSeeNeedsWork = (
  moment: Pick<StandoutMomentConstructor, "createdBy">,
  currentUserId: Uuid | null,
  hasUpdatePermission: boolean
): boolean =>
  hasUpdatePermission || (!!currentUserId && moment.createdBy === currentUserId)

/**
 * Edit (note + tags) and Generate share the same eligibility: the moment's
 * creator, or anyone with the moderation (UPDATE) permission.
 */
export const canEditMoment = (
  moment: Pick<StandoutMomentConstructor, "createdBy">,
  currentUserId: Uuid | null,
  hasUpdatePermission: boolean
): boolean =>
  hasUpdatePermission || (!!currentUserId && moment.createdBy === currentUserId)

/**
 * Approved and sent lock the same way: the words, the sentence choice,
 * Generate, and Don't share wait until the switch is turned off. Photos
 * are not part of the lock.
 */
export const isMomentShareLocked = (
  moment: Pick<StandoutMomentConstructor, "dontShare">,
  sharedWithFamily: boolean
): boolean => sharedWithFamily && moment.dontShare !== true

export const SHARE_LOCK_TOOLTIP = "Unshare to edit"
export const SHARE_LOCK_ASK_TOOLTIP =
  "Shared with parents. Ask a manager to unshare to edit."

/** Only UPDATE users get the share switch, so only they are told to unshare. */
export const shareLockTooltip = (canToggleShare: boolean): string =>
  canToggleShare ? SHARE_LOCK_TOOLTIP : SHARE_LOCK_ASK_TOOLTIP

/** A blank teacher note is a prompt to write one. An existing note is Edit. */
export const editAffordanceLabel = (
  note: string | null | undefined
): "Edit" | "add message" => (note?.trim() ? "Edit" : "add message")

// ---------------------------------------------------------------------------
// Artwork strip selection
// ---------------------------------------------------------------------------

/**
 * Scans from this moment's class session only, newest first. A moment with
 * no class session has no strip — those pieces are not "from that session".
 * Hidden scans and scans with no display URL are omitted. The strip shows
 * the newest one and hides the rest behind See more.
 */
export const selectMomentArtwork = (
  student: { artwork?: Record<Uuid, StudentArtwork> } | undefined,
  moment: Pick<StandoutMomentConstructor, "classSessionId">
): StudentArtwork[] => {
  const sessionId = moment.classSessionId
  if (!sessionId) return []
  const allArtwork = student?.artwork ? Object.values(student.artwork) : []
  if (allArtwork.length === 0) return []

  const candidates = allArtwork.filter(
    (piece) =>
      piece.classSessionId === sessionId &&
      piece.isHidden !== true &&
      !!getArtworkDisplayUrl(piece)
  )

  return sortBy(candidates, (piece) => -piece.timeCreated)
}
